import { useState } from "react";
import { Search, Eye, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import type { ContactMessage } from "@endeks/shared";
import { useContacts, useContactStats, useMarkContactRead, useDeleteContact } from "./queries";
import { showDeleteConfirm } from "../../components/Common/DeleteConfirm";
import { QueryError } from "../../components/Common/QueryError";
import { Pagination } from "../../components/Common/Pagination";
import { errorMessage } from "@/lib/error-message";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { usePagination } from "@/lib/use-pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// ContactMessage from shared is missing createdAt — the API always returns it.
type ContactRow = ContactMessage & { id: string; createdAt: string };

const TYPE_COLORS: Record<string, string> = {
  talep: "bg-blue-100 text-blue-800",
  sikayet: "bg-red-100 text-red-800",
  oneri: "bg-green-100 text-green-800",
  diger: "bg-gray-100 text-gray-700",
};

interface DetailRow {
  label: string;
  value: string | undefined | null;
}

function DescRow({ label, value }: DetailRow) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b last:border-0">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="col-span-2 text-sm break-words">{value || "-"}</dd>
    </div>
  );
}

export default function ContactList() {
  const [search, setSearch] = useState("");
  const [selectedMessage, setSelectedMessage] = useState<ContactRow | null>(null);

  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const {
    data: rawContacts,
    isLoading,
    isError,
    error,
    refetch,
  } = useContacts(debouncedSearch ? { search: debouncedSearch } : undefined);
  const contacts: ContactRow[] = (rawContacts ?? []) as ContactRow[];
  const { pageItems, page, totalPages, totalItems, setPage } = usePagination(
    contacts,
    20,
    debouncedSearch,
  );

  const { data: stats } = useContactStats();
  const markReadMutation = useMarkContactRead();
  const deleteMutation = useDeleteContact();

  function handleOpen(record: ContactRow) {
    setSelectedMessage(record);
    if (!record.read) {
      markReadMutation.mutate(record.id);
    }
  }

  function handleMarkRead(id: string) {
    markReadMutation.mutate(id, {
      onSuccess: () => toast.success("Okundu olarak işaretlendi"),
      onError: (e: unknown) => toast.error(errorMessage(e, "İşaretlenemedi")),
    });
  }

  function handleDelete(id: string) {
    showDeleteConfirm({
      onConfirm: () => {
        deleteMutation.mutate(id, {
          onSuccess: () => toast.success("Mesaj silindi"),
          onError: (e: unknown) => toast.error(errorMessage(e, "Silinemedi")),
        });
      },
    });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">İletişim Mesajları</h1>
          {stats && stats.unread > 0 && (
            <Badge variant="destructive">{stats.unread} okunmamış</Badge>
          )}
        </div>
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError && !rawContacts ? (
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : contacts.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
          {debouncedSearch ? "Arama sonucu bulunamadı." : "Henüz mesaj yok."}
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-6" />
                <TableHead>Gönderen</TableHead>
                <TableHead>E-posta</TableHead>
                <TableHead>Konu</TableHead>
                <TableHead className="w-28">Tür</TableHead>
                <TableHead className="w-28">Tarih</TableHead>
                <TableHead className="w-36 text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((contact) => (
                <TableRow
                  key={contact.id}
                  className={!contact.read ? "font-medium bg-blue-50/50" : undefined}
                >
                  <TableCell>
                    {!contact.read && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                  </TableCell>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{contact.email}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{contact.subject}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        TYPE_COLORS[contact.type] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {contact.type}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(contact.createdAt).toLocaleDateString("tr-TR")}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleOpen(contact)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {!contact.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkRead(contact.id)}
                          title="Okundu işaretle"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(contact.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={!!selectedMessage}
        onOpenChange={(open) => {
          if (!open) setSelectedMessage(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Mesaj Detayı</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <dl className="divide-y">
              <DescRow label="Gönderen" value={selectedMessage.name} />
              <DescRow label="E-posta" value={selectedMessage.email} />
              <DescRow label="Şirket" value={selectedMessage.company} />
              <DescRow label="Telefon" value={selectedMessage.phone} />
              <DescRow label="Konu" value={selectedMessage.subject} />
              <DescRow label="Tür" value={selectedMessage.type} />
              <div className="grid grid-cols-3 gap-2 py-2 border-b last:border-0">
                <dt className="text-sm font-medium text-muted-foreground">Mesaj</dt>
                <dd className="col-span-2 text-sm whitespace-pre-wrap">
                  {selectedMessage.message}
                </dd>
              </div>
              <DescRow
                label="Tarih"
                value={new Date(selectedMessage.createdAt).toLocaleString("tr-TR")}
              />
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
